import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0a0a0e] px-4 py-3 text-white placeholder:text-neutral-600 outline-none transition-colors focus:border-[#e6b800]/70";

function PasswordField({ id, inputTestId, toggleTestId, label, value, onChange, show, onToggle, minLength }) {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          data-testid={inputTestId}
          type={show ? "text" : "password"}
          className={`${inputCls} pr-12`}
          value={value}
          onChange={onChange}
          required
          minLength={minLength}
        />
        <button
          type="button"
          onClick={onToggle}
          data-testid={toggleTestId}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 transition-colors hover:text-[#e6b800]"
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

export default function ChangePasswordModal({
  onClose,
  title = "Change password",
  description = "This password signs you into the Owner Dashboard.",
  testIdPrefix = "admin",
  modalTestId = "change-password-modal",
}) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      await api.post(
        "/auth/change-password",
        { current_password: current, new_password: next },
        { withCredentials: true }
      );
      toast.success("Password updated. Use the new one next time.");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-5 backdrop-blur-sm"
      data-testid={modalTestId}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.form
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        onSubmit={submit}
        className="card-glow w-full max-w-md rounded-2xl p-8"
      >
        <div className="flex items-start justify-between">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/10 text-[#e6b800]">
            <KeyRound size={18} />
          </span>
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testIdPrefix}-change-password-close-button`}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-neutral-400 transition-colors hover:border-[#e6b800]/60 hover:text-[#e6b800]"
            aria-label="Close"
          >
            <X size={15} />
          </button>
        </div>

        <h2 className="mt-4 font-display text-xl font-bold uppercase text-white">{title}</h2>
        <p className="mt-1.5 text-xs leading-relaxed text-neutral-500">{description}</p>

        <div className="mt-6 space-y-4">
          <PasswordField
            id={`${testIdPrefix}-cp-current`}
            inputTestId={`${testIdPrefix}-current-password-input`}
            toggleTestId={`${testIdPrefix}-current-password-toggle`}
            label="Current password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            show={showCurrent}
            onToggle={() => setShowCurrent((v) => !v)}
          />
          <PasswordField
            id={`${testIdPrefix}-cp-new`}
            inputTestId={`${testIdPrefix}-new-password-input`}
            toggleTestId={`${testIdPrefix}-new-password-toggle`}
            label="New password (min 8 characters)"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            show={showNext}
            onToggle={() => setShowNext((v) => !v)}
            minLength={8}
          />
          <PasswordField
            id={`${testIdPrefix}-cp-confirm`}
            inputTestId={`${testIdPrefix}-confirm-password-input`}
            toggleTestId={`${testIdPrefix}-confirm-password-toggle`}
            label="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            show={showConfirm}
            onToggle={() => setShowConfirm((v) => !v)}
            minLength={8}
          />
        </div>

        <button
          type="submit"
          data-testid={`${testIdPrefix}-change-password-submit-button`}
          disabled={saving}
          className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-[#e6b800] px-6 py-3.5 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] disabled:opacity-60"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={14} />}
          {saving ? "Updating…" : "Update password"}
        </button>
      </motion.form>
    </div>
  );
}
